import { createContext, useContext } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Divider as MantineDivider,
  Text,
} from '@mantine/core';

import { IconArchive, IconEye } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/components/UI/Button/Button';
import { Select } from '../../../shared/components/UI/Select/Select';
import { Tooltip } from '../../../shared/components/UI/Tooltip/Tooltip';

import CadFileIcon from '../../../shared/assets/documents_icons/cad_file.svg?react';
import DbFileIcon from '../../../shared/assets/documents_icons/db_file.svg?react';
import GantFileIcon from '../../../shared/assets/documents_icons/gant_file.svg?react';
import ImageFileIcon from '../../../shared/assets/documents_icons/image_file.svg?react';
import MiscFileIcon from '../../../shared/assets/documents_icons/misc_file.svg?react';
import MovieFileIcon from '../../../shared/assets/documents_icons/movie_file.svg?react';
import PdfFileIcon from '../../../shared/assets/documents_icons/pdf_file.svg?react';
import SlideFileIcon from '../../../shared/assets/documents_icons/slide_file.svg?react';
import SpreadsheetFileIcon from '../../../shared/assets/documents_icons/spreadsheet_file.svg?react';
import TextFileIcon from '../../../shared/assets/documents_icons/text_file.svg?react';
import ZipFileIcon from '../../../shared/assets/documents_icons/zip_file.svg?react';
import type { BidResponseDocument } from '../../../shared/entities/BidResponseDocument';
import { BidResponseDocumentType as BidResponseDocumentTypeEnum } from '../../../shared/entities/BidResponseDocument';
import type { TengoDocument } from '../../../shared/entities/TengoDocument';
import type { WorkspaceDocument } from '../../../shared/entities/WorkspaceDocuments';
import { WorkspaceDocumentType as WorkspaceDocumentTypeEnum } from '../../../shared/entities/WorkspaceDocuments';
import {
  useArchiveBidResponseDocumentMutation,
  useRestoreBidResponseDocumentMutation,
} from '../../bidResponse/components/vault/hooks/bidResponseDocument.hook';
import {
  useArchiveWorkspaceDocumentMutation,
  useRecoverWorkspaceDocumentMutation,
} from '../../bidResponse/components/vault/hooks/workspaceDocuments.hook';
import { DownloadFileButton } from './DownloadFileButton';

type DocumentItemProps<T extends TengoDocument> = {
  document: T;
  onClick?: (document: T) => void;
  children: React.ReactNode;
};

type DocumentItemContext<T extends TengoDocument> = {
  document: T;
  isArchived: boolean;
};

const DocumentContext =
  createContext<DocumentItemContext<TengoDocument> | null>(null);

function useDocumentContext<T extends TengoDocument>(): DocumentItemContext<T> {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error(
      'useDocumentContext must be used within a DocumentItemProvider',
    );
  }
  return context as DocumentItemContext<T>;
}

function DocumentItem<T extends TengoDocument>({
  document,
  onClick,
  children,
}: DocumentItemProps<T>) {
  const isArchived = 'archivedAt' in document ? !!document.archivedAt : false;

  return (
    <DocumentContext.Provider value={{ document, isArchived }}>
      <Group
        noWrap
        px="03"
        py="02"
        spacing="04"
        mr="04"
        w="100%"
        sx={theme => ({
          borderRadius: theme.radius.md,
          '.hover-content': {
            display: 'none',
          },
          ':hover': {
            backgroundColor: theme.colors.gray[0],
            cursor: 'pointer',
            '.hover-content': {
              display: 'block',
            },
          },
        })}
        onClick={() => onClick?.(document)}
      >
        {children}
      </Group>
    </DocumentContext.Provider>
  );
}

type DocumentItemSubComponentProps = {
  displayOnHover?: boolean;
};

const Icon = () => {
  const { document, isArchived } = useDocumentContext();
  const icon = getDocumentIcon(document.originalName);

  return (
    <Box
      h="36px"
      sx={{
        opacity: isArchived ? 0.6 : 1,
      }}
    >
      {icon}
    </Box>
  );
};

const WorkspaceDocumentArchiveIcon = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { document } = useDocumentContext<WorkspaceDocument>();
  const { mutate: archiveWorkspaceDocument } =
    useArchiveWorkspaceDocumentMutation();

  const handleArchive = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    archiveWorkspaceDocument(document.id);
  };

  return (
    <WithHover displayOnHover={displayOnHover}>
      <ArchiveIcon onClick={handleArchive} />
    </WithHover>
  );
};

const BidResponseDocumentArchiveIcon = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { document } = useDocumentContext<BidResponseDocument>();
  const { mutate: archiveBidResponseDocument } =
    useArchiveBidResponseDocumentMutation(document.bidResponseId);

  const handleArchive = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    archiveBidResponseDocument(document.id);
  };

  return (
    <WithHover displayOnHover={displayOnHover}>
      <ArchiveIcon onClick={handleArchive} />
    </WithHover>
  );
};

const ArchiveIcon = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <ActionIcon
      size="sm"
      onClick={onClick}
      sx={theme => ({
        ':hover': { background: theme.colors.gray[1] },
      })}
    >
      <IconArchive size={16} />
    </ActionIcon>
  );
};

const BidResponseDocumentRestoreIcon = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { t } = useTranslation('common');
  const { document } = useDocumentContext<BidResponseDocument>();
  const { mutate: restoreBidResponseDocument } =
    useRestoreBidResponseDocumentMutation(document.bidResponseId);

  const handleRestore = () => {
    restoreBidResponseDocument(document.id);
  };
  return (
    <WithHover displayOnHover={displayOnHover}>
      <Button variant="light" color="primary" size="xs" onClick={handleRestore}>
        {t('restore')}
      </Button>
    </WithHover>
  );
};

const WorkspaceDocumentRestoreIcon = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { t } = useTranslation('common');
  const { document } = useDocumentContext<WorkspaceDocument>();
  const { mutate: recoverWorkspaceDocument } =
    useRecoverWorkspaceDocumentMutation();

  const handleRestore = () => {
    recoverWorkspaceDocument(document.id);
  };
  return (
    <WithHover displayOnHover={displayOnHover}>
      <Button variant="light" color="primary" size="xs" onClick={handleRestore}>
        {t('restore')}
      </Button>
    </WithHover>
  );
};

const BidResponseDocumentType = () => {
  const { t } = useTranslation(['entities', 'shared']);
  const { document, isArchived } = useDocumentContext<BidResponseDocument>();

  return (
    <Text variant="xs" fw={600} c={isArchived ? 'gray.5' : 'gray.8'}>
      {t(`documentTypes.${document.type || 'UNKNOWN'}`)}
    </Text>
  );
};

const WorkspaceDocumentType = () => {
  const { t } = useTranslation(['entities', 'shared']);
  const { document, isArchived } = useDocumentContext<WorkspaceDocument>();

  return (
    <Text variant="xs" fw={600} c={isArchived ? 'gray.5' : 'gray.8'}>
      {t(`documentTypes.${document.type || 'UNKNOWN'}`)}
    </Text>
  );
};

const TypeBadge = () => {
  const { document } = useDocumentContext();

  return (
    <Badge
      bg="gray.1"
      c="gray.8"
      variant="filled"
      size="sm"
      ml="-8px"
      sx={{ flexShrink: 0 }}
    >
      {document.type}
    </Badge>
  );
};

const Name = () => {
  const { document, isArchived } = useDocumentContext();

  return (
    <Tooltip
      withArrow
      arrowSize={8}
      arrowRadius={2}
      position="bottom-start"
      arrowPosition="center"
      radius="md"
      openDelay={500}
      content={
        <Text variant="xs" fw={600}>
          {document.prettyName}
        </Text>
      }
    >
      <Text
        variant="sm"
        fw={400}
        c={isArchived ? 'gray.6' : 'gray.9'}
        lineClamp={1}
        sx={{
          flexShrink: 1,
        }}
      >
        {document.prettyName}
      </Text>
    </Tooltip>
  );
};

function WorkspaceDocumentTypeSelect({
  onChange,
}: {
  onChange: (type: WorkspaceDocumentTypeEnum) => void;
}) {
  const { document } = useDocumentContext();
  const { t } = useTranslation(['entities', 'shared']);

  const data = Object.values(WorkspaceDocumentTypeEnum)
    .map(docType => ({
      value: docType,
      label: t(`documentTypes.${docType}`),
    }))
    .filter(item => item.value !== WorkspaceDocumentTypeEnum.UNKNOWN)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Select
      data={data}
      placeholder={t('shared:documentItem.typeSelect.placeholder')}
      value={document.type}
      size="sm"
      variant="filled"
      onChange={onChange}
      width="200px"
    />
  );
}

function BidResponseDocumentTypeSelect({
  onChange,
}: {
  onChange: (type: BidResponseDocumentTypeEnum) => void;
}) {
  const { document } = useDocumentContext<BidResponseDocument>();
  const { t } = useTranslation(['entities', 'shared']);

  const data = Object.values(BidResponseDocumentTypeEnum)
    .map(docType => ({
      value: docType,
      label: t(`documentTypes.${docType}`),
    }))
    .filter(item => item.value !== BidResponseDocumentTypeEnum.UNKNOWN)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Select
      data={data}
      placeholder={t('shared:documentItem.typeSelect.placeholder')}
      value={document.type}
      size="xs"
      variant="filled"
      onChange={onChange}
      width="200px"
    />
  );
}

const Divider = ({ displayOnHover = false }: DocumentItemSubComponentProps) => {
  return (
    <WithHover displayOnHover={displayOnHover}>
      <MantineDivider color="gray.3" orientation="vertical" h="04" my="auto" />
    </WithHover>
  );
};

const DocumentDownloadButton = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { document } = useDocumentContext();

  return (
    <WithHover displayOnHover={displayOnHover}>
      <DownloadFileButton
        S3FileURL={document.S3FileURL}
        prettyName={document.prettyName}
      />
    </WithHover>
  );
};

const PreviewButton = ({
  displayOnHover = false,
}: DocumentItemSubComponentProps) => {
  const { t } = useTranslation();

  return (
    <WithHover displayOnHover={displayOnHover}>
      <Button
        variant="white"
        color="primary"
        size="xs"
        leftIcon={<IconEye size={16} />}
      >
        {t('preview')}
      </Button>
    </WithHover>
  );
};

const Spacer = () => {
  return <Box sx={{ flexGrow: 1 }} />;
};

const WithHover = ({
  displayOnHover = false,
  children,
}: {
  displayOnHover: boolean;
  children: React.ReactNode;
}) => {
  if (!displayOnHover) return <>{children}</>;

  return <Box className="hover-content">{children}</Box>;
};

const getDocumentIcon = (title: string) => {
  const fileExtension = title.split('.').pop();
  const style = { height: '36px' };
  switch (fileExtension) {
    case 'pdf':
      return <PdfFileIcon style={style} />;
    case 'dotx':
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
    case 'odt':
      return <TextFileIcon style={style} />;
    case 'xls':
    case 'xlsx':
    case 'ods':
    case 'csv':
    case 'xml':
      return <SpreadsheetFileIcon style={style} />;
    case 'ppt':
    case 'pptx':
    case 'odp':
    case 'pps':
      return <SlideFileIcon style={style} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'bmp':
    case 'tiff':
    case 'tif':
    case 'svg':
      return <ImageFileIcon style={style} />;
    case 'cad':
    case 'dwg':
    case 'dxf':
    case 'dwf':
    case 'pat':
      return <CadFileIcon style={style} />;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return <ZipFileIcon style={style} />;
    case 'mpp':
    case 'mpx':
    case 'xer':
      return <GantFileIcon style={style} />;
    case 'mdb':
    case 'accdb':
    case 'sql':
      return <DbFileIcon style={style} />;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'mp3':
    case 'wav':
    case 'aac':
    case 'flac':
      return <MovieFileIcon style={style} />;
    default:
      return <MiscFileIcon style={style} />;
  }
};

const TypeSelect = {
  WorkspaceDocument: WorkspaceDocumentTypeSelect,
  BidResponseDocument: BidResponseDocumentTypeSelect,
};

const ArchiveButton = {
  BidResponseDocument: BidResponseDocumentArchiveIcon,
  WorkspaceDocument: WorkspaceDocumentArchiveIcon,
};

const RestoreButton = {
  BidResponseDocument: BidResponseDocumentRestoreIcon,
  WorkspaceDocument: WorkspaceDocumentRestoreIcon,
};

const Type = {
  BidResponseDocument: BidResponseDocumentType,
  WorkspaceDocument: WorkspaceDocumentType,
};

DocumentItem.Icon = Icon;
DocumentItem.TypeBadge = TypeBadge;
DocumentItem.Type = Type;
DocumentItem.TypeSelect = TypeSelect;
DocumentItem.ArchiveButton = ArchiveButton;
DocumentItem.RestoreButton = RestoreButton;
DocumentItem.Name = Name;
DocumentItem.Divider = Divider;
DocumentItem.DocumentDownloadButton = DocumentDownloadButton;
DocumentItem.PreviewButton = PreviewButton;
DocumentItem.Spacer = Spacer;

export default DocumentItem;
